import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { loginUser, loginWithGoogle, loginWithMeta, ApiError } from '../../api/client';
import { setSession } from '../../auth/authStore';
import GoogleSignInButton from './GoogleSignInButton';
import MetaSignInButton from './MetaSignInButton';

/** Pulls the auth token out of the login payload, tolerating field-name drift. */
function extractToken(payload) {
  return (
    payload?.token ||
    payload?.accessToken ||
    payload?.jwt ||
    payload?.access_token ||
    null
  );
}

/**
 * Sign-in form. Accepts an email or username plus a password, calls
 * POST /users/login, stores the returned token (honouring "Mantener sesión")
 * and sends the visitor on to wherever they were originally headed.
 */
function LoginForm({ initialIdentifier = '', onSwitchToRegister, onNeedsActivation }) {
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/';
  const hide = process.env.REACT_APP_META_BUTTON_HIDE === 'true';
  const [values, setValues] = useState({
    usernameOrEmail: initialIdentifier,
    password: '',
  });
  const [remember, setRemember] = useState(true);
  const [reveal, setReveal] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [errors, setErrors] = useState({});
  const [banner, setBanner] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const update = (field) => (event) => {
    setValues((prev) => ({ ...prev, [field]: event.target.value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
    if (banner) setBanner('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setBanner('');

    const nextErrors = {};
    if (!values.usernameOrEmail.trim())
      nextErrors.usernameOrEmail = 'Introduce tu correo o usuario.';
    if (!values.password) nextErrors.password = 'Introduce tu contraseña.';
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    setSubmitting(true);
    try {
      const payload = await loginUser({
        usernameOrEmail: values.usernameOrEmail.trim(),
        password: values.password,
      });
      const token = extractToken(payload);
      if (!token) {
        setBanner('No hemos recibido una sesión válida. Inténtalo de nuevo.');
        return;
      }
      setSession(token, { remember });
      navigate(from, { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        // Valid credentials but the account is still unverified: the backend
        // answers 403 "Account not activated". Send the user to the activation
        // step instead of surfacing an error.
        if (err.status === 403 && onNeedsActivation) {
          onNeedsActivation(values.usernameOrEmail.trim());
          return;
        }
        // A 401 with no useful detail still means the credentials didn't match.
        setBanner(
          err.status === 401 && !err.detail
            ? 'Correo o contraseña incorrectos.'
            : err.detail || err.message
        );
      } else {
        setBanner('Ha ocurrido un error inesperado. Inténtalo de nuevo.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Shared tail for the social flows: swap the provider token for a FLIK
  // session and continue exactly like a password login. A social account is
  // already verified, so there's no activation hop.
  const completeSocialLogin = async (exchange, fallbackMessage) => {
    setBanner('');
    setSubmitting(true);
    try {
      const payload = await exchange();
      const token = extractToken(payload);
      if (!token) {
        setBanner('No hemos recibido una sesión válida. Inténtalo de nuevo.');
        return;
      }
      setSession(token, { remember });
      navigate(from, { replace: true });
    } catch (err) {
      setBanner(
        err instanceof ApiError ? err.detail || err.message : fallbackMessage
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Google (ID-token flow): GIS hands us the credential, we post it to
  // POST /users/google.
  const handleGoogleCredential = (idToken) =>
    completeSocialLogin(
      () => loginWithGoogle({ idToken }),
      'No hemos podido entrar con Google. Inténtalo de nuevo.'
    );

  // Meta (Facebook Login): the FB SDK hands us a user access token, we post it
  // to POST /users/meta.
  const handleMetaAccessToken = (accessToken) =>
    completeSocialLogin(
      () => loginWithMeta({ accessToken }),
      'No hemos podido entrar con Meta. Inténtalo de nuevo.'
    );

  const inputClass = (field) =>
    `AuthInput${errors[field] ? ' AuthInput--invalid' : ''}`;

  return (
    <form className="AuthForm-fields" onSubmit={handleSubmit} noValidate>
      <h2 className="AuthForm-heading">Bienvenido de nuevo</h2>
      <div className="AuthField">
        <label className="AuthLabel" htmlFor="login-id">Email o usuario</label>
        <input
          id="login-id"
          className={inputClass('usernameOrEmail')}
          type="text"
          placeholder="tu@email.com"
          autoComplete="username"
          autoFocus
          value={values.usernameOrEmail}
          onChange={update('usernameOrEmail')}
        />
        {errors.usernameOrEmail && (
          <span className="AuthError">{errors.usernameOrEmail}</span>
        )}
      </div>

      <div className="AuthField Login-field">
        <label className="AuthLabel" htmlFor="login-password">Contraseña</label>
        <div className="AuthInputWrap">
          <input
            id="login-password"
            className={inputClass('password')}
            type={reveal ? 'text' : 'password'}
            placeholder="••••••••"
            autoComplete="current-password"
            value={values.password}
            onChange={update('password')}
            onFocus={() => setPasswordFocused(true)}
            onBlur={() => setPasswordFocused(false)}
          />
          {passwordFocused && values.password && (
            <button
              type="button"
              className="AuthReveal"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => setReveal((r) => !r)}
              aria-label={reveal ? 'Ocultar contraseña' : 'Mostrar contraseña'}
          >
            {reveal ? 'Ocultar' : 'Ver'}
          </button>)}
        </div>
        {errors.password && <span className="AuthError">{errors.password}</span>}
      </div>

      <div className="Login-options">
        <label className="Login-remember">
          <input
            type="checkbox"
            checked={remember}
            onChange={(event) => setRemember(event.target.checked)}
          />
          <span>Mantener sesión</span>
        </label>
        <button
          type="button"
          className="AuthLink Login-forgot"
          onClick={() =>
            setBanner('El restablecimiento de contraseña estará disponible muy pronto.')
          }
        >
          ¿Olvidaste la contraseña?
        </button>
      </div>

      {banner && <p className="AuthBanner">{banner}</p>}

      <button className="AuthSubmit" type="submit" disabled={submitting}>
        {submitting ? 'Entrando…' : 'Entrar →'}
      </button>

      <div className="Login-socials">
        <GoogleSignInButton
          onCredential={handleGoogleCredential}
          onError={setBanner}
          disabled={submitting}
        />
        { !hide && (<MetaSignInButton
          onAccessToken={handleMetaAccessToken}
          onError={setBanner}
          disabled={submitting}
        />)}
        
      </div>

      <p className="Login-note">
        Al entrar aceptas los <a href="/terminos">términos</a> de FLIK.
      </p>

      <svg
        className="Login-ridge"
        viewBox="0 0 660 40"
        preserveAspectRatio="none"
        role="presentation"
        aria-hidden="true"
        focusable="false"
      >
        <path
          d="M0 30 L120 27 L200 12 L330 22 L440 6 L560 24 L660 14"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinejoin="round"
          strokeLinecap="round"
        />
      </svg>

      <p className="Login-note Login-note--switch">
        <button type="button" className="AuthLink" onClick={onSwitchToRegister}>
          ¿No tienes cuenta?
        </button>
      </p>
    </form>
  );
}

export default LoginForm;
